// =====================================================
// PIECES — a paragraph's word diff as the pieces every output draws, each with the
// whitespace that goes before it as the source has it (tabs, line breaks, double and
// no-break spaces), and the source word it comes from (for its formatting).
//
// One rule for every writer: the .docx body, table cells and footnotes
// (docx-tracking `diffRunsXml`), and the screen and PDF page (renderer `fillDiff`). Until
// stage 2 of the engine cleanup each kept its own: the screen used the source's spacing
// while the Word writers joined every word with one space, so an edited clause lost the
// tab after "(a)" in Word and kept it on screen.
//
// Spacing (glued pieces aside): a piece that starts its word takes the whitespace before that
// word on its own side: deleted and moved-from pieces the original's, the rest the revised
// draft's. An unchanged word takes the revised draft's, so Accept All gives the revised
// spacing exactly. Where the two drafts' whitespace before an unchanged word differs in its tabs
// or line breaks (layoutOf), the Word writer tracks it (the original's deleted, the revised
// draft's inserted: Reject All gives the original's back); runs of spaces are not tracked, so
// Reject All shows the revised spacing there. (A page break is \f and a column break \v.)
//
// What sits between the pieces and is not a word (footnote marks, comments, bookmarks, links,
// fields, pictures) is placed among them by inline.ts, for the .docx writer.
// =====================================================

import type { DiffEntry, RichToken } from '../types';

/** One side of a paragraph: its words and the whitespace around them (null: single spaces). */
export interface Side { tokens: RichToken[]; gaps: string[] | null }

const gapCache = new WeakMap<object, string[] | null>();
/** The whitespace before each word of `text` and after the last one, or null if the words do
 *  not line up with `count` tokens. */
function gapsFromText(text: string, count: number): string[] | null {
  const gaps: string[] = [];
  let last = 0;
  for (const m of text.matchAll(/\S+/g)) { gaps.push(text.slice(last, m.index)); last = m.index! + m[0].length; }
  if (gaps.length !== count) return null;
  gaps.push(text.slice(last));
  return gaps;
}

/** A paragraph's (or a table cell's) side: the parser's exact gaps when it recorded them,
 *  else the whitespace of its text (no-break spaces then read as plain spaces). */
export function sideOf(x?: { text?: string; richTokens?: RichToken[]; gaps?: string[] } | null): Side {
  const tokens = x?.richTokens || [];
  if (!x) return { tokens, gaps: null };
  if (x.gaps && x.gaps.length === tokens.length + 1) return { tokens, gaps: x.gaps };
  if (gapCache.has(x)) return { tokens, gaps: gapCache.get(x)! };
  const gaps = gapsFromText(x.text || '', tokens.length);
  gapCache.set(x, gaps);
  return { tokens, gaps };
}

export interface Piece {
  entry: DiffEntry;
  /** the whitespace before the piece ('' when glued to the piece before it) */
  gap: string;
  /** true for pieces drawn from the revised draft (unchanged, inserted, moved to) */
  fromRev: boolean;
  /** the source word the piece comes from, and the piece's place in it */
  token?: RichToken;
  charIdx?: number;
  /** an unchanged piece's word in the original (its earlier formatting) */
  origToken?: RichToken;
  origCharIdx?: number;
  /** an unchanged piece's whitespace in the original, by the same rule as `gap` (a tab or line
   *  break that differs is tracked: docx-tracking `diffRunsXml`) */
  origGap?: string;
}

const fromRevSide = (t: DiffEntry['type']) => t === 'equal' || t === 'insert' || t === 'move-to';

/** The diff's pieces in order, with their whitespace. Pieces that are only whitespace (other
 *  than unchanged ones) and empty pieces are left out, as every writer did before. */
export function diffPieces(diff: DiffEntry[], orig: Side, rev: Side, opts: {
  /** drawn for reading (the screen, the PDF page, a coloured markup), not tracked */
  visual?: boolean;
} = {}): Piece[] {
  const out: Piece[] = [];
  for (const d of diff) {
    if (!d.value || (!d.value.trim() && d.type !== 'equal')) continue;
    const fromRev = fromRevSide(d.type);
    const side = fromRev ? rev : orig;
    const idx = fromRev ? d.revWordIdx : d.origWordIdx;
    // A piece that starts its side's first word takes that word's own leading whitespace (normally
    // none), wherever it falls among the pieces: for tracked changes a fallback space here made
    // Word's Accept All of "[-6-]{+7+}. Use." read " 7. Use." (until October 29, 2026). Drawn for
    // reading, a word that follows another piece with nothing before it gets a space, so
    // "[-Exhibit-]{+EXHIBIT A+}" does not read "ExhibitEXHIBIT A".
    const gapOf = (sd: Side, i: number | undefined) => {
      if (d.glued) return '';
      if (i === 0 && !(opts.visual && out.length)) return sd.gaps ? sd.gaps[0] : '';
      if (!out.length) return '';
      return (i != null && sd.gaps ? sd.gaps[i] : '') || ' ';
    };
    const gap = gapOf(side, idx);
    out.push({
      entry: d, gap, fromRev,
      token: idx != null ? side.tokens[idx] : undefined,
      charIdx: fromRev ? d.revCharIdx : d.origCharIdx,
      ...(d.type === 'equal' ? { origToken: d.origWordIdx != null ? orig.tokens[d.origWordIdx] : undefined, origCharIdx: d.origCharIdx,
        origGap: orig.gaps ? gapOf(orig, d.origWordIdx) : undefined } : {}),
    });
  }
  return out;
}

/** The revised draft's whitespace after its last word ('' when none, or unknown). */
export function trailingGap(rev: Side): string {
  return rev.gaps ? rev.gaps[rev.gaps.length - 1] || '' : '';
}

/** The layout in a stretch of whitespace: its tabs and line, page and column breaks, in order.
 *  Two drafts whose whitespace differs in this are tracked (stage 4: a tab that became a space is
 *  a tracked change, so Reject All gives the original's tab back); runs of spaces are not. */
export const layoutOf = (gap: string | undefined): string => (gap || '').replace(/[^\t\n\f\v]/g, '');

/** The whitespace before an unchanged word, tracked where the drafts differ in it: in its tabs or
 *  line breaks (layoutOf), or where only one draft has any (a word boundary on one side only: the
 *  revision deleted the words before what is now its paragraph's first word, so "Price. (c)" became
 *  "(c)"; untracked, Reject All ran "Price.(c)" together). Runs of spaces are not tracked. */
export const gapsDiffer = (orig: string | undefined, rev: string | undefined): boolean =>
  layoutOf(orig) !== layoutOf(rev) || !orig !== !rev;

/** An unchanged paragraph whose two drafts differ only in layout whitespace somewhere (their
 *  words paired one to one): it is written as an edited paragraph, so the change is tracked. */
export function layoutDiffers(o: Side, r: Side): boolean {
  if (!o.gaps || !r.gaps || o.tokens.length !== r.tokens.length || o.gaps.length !== r.gaps.length) return false;
  for (let k = 0; k < o.gaps.length; k++) if (layoutOf(o.gaps[k]) !== layoutOf(r.gaps[k])) return true;
  return false;
}

/** The word diff of a paragraph whose words are all unchanged (layoutDiffers). */
export function allEqualDiff(o: Side, r: Side): DiffEntry[] | null {
  if (o.tokens.length !== r.tokens.length || !r.tokens.length) return null;
  return r.tokens.map((t, k) => ({ type: 'equal', value: t.word, origWordIdx: k, revWordIdx: k }));
}

/** The space a plain-text view of one side of a diff (the summary, the PDF page's notes and
 *  headers) puts before an entry: none at the start or before a glued piece, else one. Such
 *  views join words with single spaces; the layout ones use `diffPieces`. (The phone's change
 *  list joins changed pieces only, and keeps a space between two changes: change-index.ts.) */
export const textGap = (d: DiffEntry, first: boolean): string => (first || d.glued ? '' : ' ');
